import {
  cardFromJson,
  cardToJson,
  type CardRecord,
  type ReviewInput,
  type ReviewRecord,
  type Snapshot,
  type StorageBackend,
} from './backend';
import type { PbSession } from './pb-auth';
import { t } from '../../strings';
import type { CardKey, Skill } from '../../types';
import { SKILLS } from '../../types';

/**
 * Shape of the progress data as this build understands it. Must match the
 * `meta.schemaVersion` record created by pb/pb_migrations/. Bump both together
 * — see docs/DEPLOY.md.
 */
export const SCHEMA_VERSION = 2;

/**
 * Thrown before any read or write when the server's meta.schemaVersion differs
 * from this build's SCHEMA_VERSION — the guard that stops a stale cached page
 * (a PWA can easily be one) from writing data the server no longer understands.
 */
export class SchemaVersionMismatchError extends Error {
  constructor(
    readonly appVersion: number,
    readonly serverVersion: number | null,
  ) {
    super(
      serverVersion === null
        ? t('app.errors.schemaMissing')
        : t('app.errors.schemaMismatch', { app: appVersion, server: serverVersion }),
    );
  }
}

type Rec = Record<string, unknown>;

const PER_PAGE = 500;

/**
 * Progress in PocketBase — the deployed source of truth. One collection each
 * for cards, reviews and settings; every record carries an `owner` relation
 * that the server's API rules use to scope reads and writes to the signed-in
 * user, so two people on the same instance never see each other's progress.
 *
 * Writes are serialized through a queue. Callers (core/db.ts) fire them without
 * awaiting so the quiz UI stays responsive, and the queue keeps a card's
 * updates in the order they happened.
 */
export class PocketBaseBackend implements StorageBackend {
  /** PocketBase record id per card key, so putCard knows create vs. update. */
  private cardIds = new Map<CardKey, string>();
  private settingsRecId: string | null = null;
  private queue: Promise<unknown> = Promise.resolve();

  constructor(private readonly session: PbSession) {}

  get label(): string {
    return t('app.backend.server', { email: this.session.email });
  }

  /** Chain `op` after all previously issued writes; its promise is the caller's. */
  private enqueue<T>(op: () => Promise<T>): Promise<T> {
    const result = this.queue.then(op, op);
    this.queue = result.catch(() => {});
    return result;
  }

  async hydrate(sinceTs: number): Promise<Snapshot> {
    await this.checkSchemaVersion();
    const [cardRecs, reviewRecs, settingsRecs] = await Promise.all([
      this.listAll('cards'),
      this.listAll('reviews', `ts >= ${sinceTs}`),
      this.listAll('settings'),
    ]);

    this.cardIds.clear();
    const cards: CardRecord[] = [];
    for (const r of cardRecs) {
      const rec = cardFrom(r);
      this.cardIds.set(rec.key, String(r['id']));
      cards.push(rec);
    }

    const settingsRec = settingsRecs[0];
    this.settingsRecId = settingsRec ? String(settingsRec['id']) : null;

    return {
      cards,
      reviews: reviewRecs.map(reviewFrom),
      settings: settingsFrom(settingsRec),
    };
  }

  putCard(rec: CardRecord): Promise<void> {
    return this.enqueue(async () => {
      const body: Rec = {
        key: rec.key,
        code: rec.code,
        skill: rec.skill,
        card: cardToJson(rec.card),
        due: rec.due.toISOString(),
      };
      const existing = this.cardIds.get(rec.key);
      if (existing) {
        await this.patchNow('cards', existing, body);
      } else {
        const created = await this.createNow('cards', body);
        this.cardIds.set(rec.key, String(created['id']));
      }
    });
  }

  deleteCard(key: CardKey): Promise<void> {
    return this.enqueue(async () => {
      const id = this.cardIds.get(key);
      if (!id) return;
      await this.removeNow('cards', id);
      this.cardIds.delete(key);
    });
  }

  logReview(rec: ReviewInput): Promise<ReviewRecord> {
    return this.enqueue(async () => {
      const created = await this.createNow('reviews', { ...rec });
      return { ...rec, id: String(created['id']) };
    });
  }

  deleteReview(id: string): Promise<void> {
    return this.enqueue(() => this.removeNow('reviews', id));
  }

  saveSettings(settings: Record<string, unknown>): Promise<void> {
    return this.enqueue(async () => {
      if (this.settingsRecId === null) {
        const created = await this.createNow('settings', { data: settings });
        this.settingsRecId = String(created['id']);
      } else {
        await this.patchNow('settings', this.settingsRecId, { data: settings });
      }
    });
  }

  async allReviews(): Promise<ReviewRecord[]> {
    return (await this.listAll('reviews')).map(reviewFrom);
  }

  /** Wholesale replace: wipe this user's records, then recreate from `snap`. */
  replaceAll(snap: Snapshot): Promise<void> {
    return this.enqueue(async () => {
      for (const c of ['cards', 'reviews', 'settings']) {
        for (const r of await this.listAll(c)) await this.removeNow(c, String(r['id']));
      }
      this.cardIds.clear();
      this.settingsRecId = null;

      for (const rec of snap.cards) {
        const created = await this.createNow('cards', {
          key: rec.key,
          code: rec.code,
          skill: rec.skill,
          card: cardToJson(rec.card),
          due: rec.due.toISOString(),
        });
        this.cardIds.set(rec.key, String(created['id']));
      }
      for (const r of snap.reviews) {
        await this.createNow('reviews', {
          key: r.key,
          rating: r.rating,
          correct: r.correct,
          // A text field, so an unrecorded pick is the empty string on the
          // server and undefined again on the way back out (see reviewFrom).
          picked: r.picked ?? '',
          ts: r.ts,
        });
      }
      const created = await this.createNow('settings', { data: snap.settings });
      this.settingsRecId = String(created['id']);
    });
  }

  // ---- low-level helpers ----

  /**
   * Corruption guard — runs before hydrate() reads anything, and every write
   * path only exists after a successful hydrate().
   */
  private async checkSchemaVersion(): Promise<void> {
    const res = await this.session.request(
      `/api/collections/meta/records?perPage=1&skipTotal=1&filter=${encodeURIComponent('key="schema"')}`,
    );
    const data = (await res.json()) as { items: Rec[] };
    const server = data.items[0]?.['schemaVersion'];
    if (typeof server !== 'number') throw new SchemaVersionMismatchError(SCHEMA_VERSION, null);
    if (server !== SCHEMA_VERSION) throw new SchemaVersionMismatchError(SCHEMA_VERSION, server);
  }

  private async listAll(collection: string, filter = ''): Promise<Rec[]> {
    const items: Rec[] = [];
    for (let page = 1; ; page++) {
      const q =
        `perPage=${PER_PAGE}&page=${page}&skipTotal=1` +
        (filter ? `&filter=${encodeURIComponent(`(${filter})`)}` : '');
      const res = await this.session.request(`/api/collections/${collection}/records?${q}`);
      const data = (await res.json()) as { items: Rec[] };
      items.push(...data.items);
      if (data.items.length < PER_PAGE) return items;
    }
  }

  // *Now variants run immediately — only for use inside an enqueue()d op.

  private async createNow(collection: string, body: Rec): Promise<Rec> {
    const res = await this.session.request(`/api/collections/${collection}/records`, {
      method: 'POST',
      body: JSON.stringify({ ...body, owner: this.session.userId }),
    });
    return (await res.json()) as Rec;
  }

  private async patchNow(collection: string, id: string, body: Rec): Promise<void> {
    await this.session.request(`/api/collections/${collection}/records/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  private async removeNow(collection: string, id: string): Promise<void> {
    await this.session.request(`/api/collections/${collection}/records/${id}`, {
      method: 'DELETE',
    });
  }
}

// ---- record → model mapping ------------------------------------------------

function cardFrom(r: Rec): CardRecord {
  const card = cardFromJson(r['card']);
  const skill = r['skill'] as Skill;
  return {
    key: String(r['key']) as CardKey,
    code: String(r['code'] ?? ''),
    skill: SKILLS.includes(skill) ? skill : 'sound',
    card,
    // `due` is denormalized for querying; the card's own value is canonical.
    due: card.due,
  };
}

function reviewFrom(r: Rec): ReviewRecord {
  // PocketBase answers with '' for a text field that was never written — every
  // review from before the v2 migration, and every flip-card grade since. The
  // model says "no pick" with undefined, so the empty string is dropped here
  // rather than left to read as a species code of length zero.
  const picked = String(r['picked'] ?? '');
  return {
    id: String(r['id']),
    key: String(r['key']) as CardKey,
    rating: Number(r['rating'] ?? 0),
    correct: r['correct'] === true,
    ...(picked ? { picked } : {}),
    ts: Number(r['ts'] ?? 0),
  };
}

function settingsFrom(r: Rec | undefined): Record<string, unknown> {
  const data = r?.['data'];
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    return { ...(data as Record<string, unknown>) };
  }
  return {};
}
